import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";
import { useI18n } from "@/lib/i18n";
import { useActiveWorkspace } from "@/contexts/ActiveWorkspace";
import { trpc, type RouterOutputs } from "@/lib/trpc";
import { weekStartUTC, weekKey } from "@shared/workweek";
import {
  initialWeekOffsetFromUrl,
  initialWeekTargetFromUrl,
  syncWeekUrlParam,
  weekOffsetForTarget,
} from "@/lib/weekParam";
import { TabScaffold } from "@/components/tabs/TabScaffold";
import { AppTopBar } from "@/components/home/AppTopBar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { importanceToneClass } from "@/lib/importance";
import { WeeklyReportPrintDocument } from "@/components/team/WeeklyReportPrintDocument";
import { toPrintModel } from "@/components/team/weeklyReportPrintModel";
import {
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Star,
  ArrowLeft,
  FileText,
  Pencil,
  RefreshCw,
  CheckCircle2,
  Undo2,
  Printer,
  Mail,
  MoreHorizontal,
} from "lucide-react";

const NOW = new Date();

/**
 * Stage 6B (weekly operating model) — STORED Weekly Team Report page.
 *
 * The stored, revisioned report is now the source of truth (the Stage-4
 * render-time roll-up became the deterministic draft composer server-side).
 * MEMBERS see APPROVED content only — the server never even serializes draft
 * keys for them. AUTHORITY generates a deterministic draft, edits it, approves
 * it (publish), reopens it (unpublish); an approved report stays visible while
 * a newer draft exists; the "new shared activity" staleness banner is an
 * authoring signal computed server-side (no fingerprint on the wire).
 *
 * PRIVACY (contract-tested): built ONLY from intentionally-shared team data —
 * no private personal work, no relevance marks, no counts, no member
 * participation, no author names (timestamp-only), no LLM in this stage.
 */
type GetOut = RouterOutputs["weeklyTeamReports"]["get"];
type AuthorityOut = Extract<GetOut, { draft?: unknown }>;
type ReportContent = NonNullable<GetOut["approved"]>["content"];

type ReportStatus = "on_track" | "needs_attention" | "blocked" | "completed_this_week";

const statusLabel = (s: ReportStatus, ar: boolean): string =>
  ({
    on_track: ar ? "على المسار" : "On track",
    needs_attention: ar ? "يحتاج انتباهًا" : "Needs attention",
    blocked: ar ? "معطَّل" : "Blocked",
    completed_this_week: ar ? "أُنجز هذا الأسبوع" : "Completed this week",
  })[s];

const statusToneClass = (s: ReportStatus): string =>
  ({
    on_track: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    needs_attention: "bg-amber-500/15 text-amber-800 dark:text-amber-300",
    blocked: "bg-orange-500/15 text-orange-800 dark:text-orange-300",
    completed_this_week: "bg-coral-soft/70 text-coral",
  })[s];

const impLabel = (imp: number, ar: boolean) =>
  (ar ? ["", "عالٍ", "متوسط", "منخفض"] : ["", "High", "Medium", "Low"])[imp] ?? "";

/** Render one stored ReportContent (approved OR draft) — content only. */
function ReportContentView({
  content,
  isAr,
  aiPrepared,
}: {
  content: ReportContent;
  isAr: boolean;
  /** W4 — draft context only: label the AI-authored intro "AI-prepared draft".
   *  Never applied to an approved report (over-AI-branding is avoided). */
  aiPrepared?: boolean;
}) {
  const t = (en: string, ar: string) => (isAr ? ar : en);
  // W-1 — a collapsed native <details> is omitted from Chromium's print output
  // (the UA hides ::details-content; a descendant's `display:block` cannot
  // reveal it). Force Direction Overview OPEN for the print/PDF only, then
  // restore the reader's prior open/closed state — never a permanent on-screen
  // expansion, and only this one section (not "How this report is built").
  const directionsRef = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const el = directionsRef.current;
    if (!el || typeof window === "undefined") return;
    let prevOpen = false;
    const onBeforePrint = () => {
      prevOpen = el.open;
      el.open = true;
    };
    const onAfterPrint = () => {
      el.open = prevOpen;
    };
    window.addEventListener("beforeprint", onBeforePrint);
    window.addEventListener("afterprint", onAfterPrint);
    return () => {
      window.removeEventListener("beforeprint", onBeforePrint);
      window.removeEventListener("afterprint", onAfterPrint);
    };
  }, []);
  return (
    <>
      {/* W4 — Executive summary (the AI-authored intro). Prominent but concise;
          labelled AI-prepared ONLY in the draft review context. Absent intro =
          a compact neutral state (no large empty card). */}
      {content.intro ? (
        <div
          className="rounded-3xl border border-border/60 bg-card p-5 shadow-sm"
          data-testid="report-intro"
        >
          {aiPrepared ? (
            <p
              className="text-[11px] font-medium text-muted-foreground mb-1"
              data-testid="report-intro-ai-label"
            >
              {t("AI-prepared draft. Review before approval.", "مسودة مُعدّة بالذكاء الاصطناعي. راجعها قبل الاعتماد.")}
            </p>
          ) : null}
          <p className="text-sm whitespace-pre-wrap break-words">{content.intro}</p>
        </div>
      ) : null}
      {content.thinData ? (
        <p className="text-xs text-muted-foreground px-1" data-testid="report-thin">
          {t(
            "Shared information for this week is limited.",
            "المعلومات المُشارَكة لهذا الأسبوع محدودة.",
          )}
        </p>
      ) : null}

      <div
        className="rounded-3xl border border-border/60 bg-card p-5 shadow-sm"
        data-testid="report-focus-section"
      >
        <h3 className="font-medium text-sm mb-2 flex items-center gap-1.5">
          <Star className="h-3.5 w-3.5 text-coral" />
          {t("Team focus this week", "تركيز الفريق هذا الأسبوع")}
        </h3>
        {content.focus.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="report-focus-empty">
            {t("No weekly focus set for this week.", "لا يوجد تركيز محدَّد لهذا الأسبوع.")}
          </p>
        ) : (
          <ul className="space-y-2">
            {content.focus.map((f, i) => (
              <li key={i} data-testid={`report-focus-item-${i}`}>
                <span className="text-sm font-medium break-words">{f.title}</span>
                {f.note ? (
                  <p className="text-xs text-muted-foreground mt-0.5">{f.note}</p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* W3 — Weekly progress. When HUMAN-declared per-priority statuses
          exist they ARE the progress section (one item per declaration,
          verbatim human content, never AI-labelled); the legacy team-week
          update then renders separately as "Additional weekly context" so the
          same progress never appears twice. Historical content without
          declarations keeps the exact legacy rendering. */}
      {content.declarations && content.declarations.length > 0 ? (
        <>
          <div
            className="rounded-3xl border border-border/60 bg-card p-5 shadow-sm"
            data-testid="report-declarations-section"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-x-2 mb-2">
              <h3 className="font-medium text-sm">{t("Weekly progress", "التقدم الأسبوعي")}</h3>
              {/* W4 — human-ownership label so the reader knows this status is
                  DECLARED, never AI-computed. */}
              <span
                className="text-[11px] text-muted-foreground"
                data-testid="report-declarations-owner-label"
              >
                {t("Human-declared status", "حالة مُعلنة بشريًا")}
              </span>
            </div>
            <ul className="space-y-3">
              {content.declarations.map((d, i) => (
                <li key={i} data-testid={`report-declaration-${i}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium min-w-0 break-words">
                      {d.teamPriorityTitle}
                    </span>
                    <Badge
                      variant="secondary"
                      className={cn("text-[11px] shrink-0", statusToneClass(d.status))}
                      data-testid={`report-declaration-status-${i}`}
                    >
                      {statusLabel(d.status, isAr)}
                    </Badge>
                  </div>
                  {d.note ? (
                    <p className="mt-0.5 text-sm whitespace-pre-wrap break-words">{d.note}</p>
                  ) : null}
                  {d.blocker ? (
                    <p className="mt-0.5 text-xs text-muted-foreground break-words">
                      {t("Blocker", "العائق")}: {d.blocker}
                    </p>
                  ) : null}
                  {d.nextStep ? (
                    <p className="mt-0.5 text-xs text-muted-foreground break-words">
                      {t("Next step", "الخطوة التالية")}: {d.nextStep}
                    </p>
                  ) : null}
                  {d.decisionRequired ? (
                    <p className="mt-0.5 text-xs text-muted-foreground break-words">
                      {t("Decision required", "قرار مطلوب")}: {d.decisionRequired}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
          {content.progress != null ? (
            <div
              className="rounded-3xl border border-border/60 bg-card p-5 shadow-sm"
              data-testid="report-legacy-context-section"
            >
              <h3 className="font-medium text-sm mb-2">
                {t("Additional weekly context", "سياق أسبوعي إضافي")}
              </h3>
              <div className="space-y-1">
                <Badge
                  variant="secondary"
                  className={cn("text-[11px]", statusToneClass(content.progress.status))}
                >
                  {statusLabel(content.progress.status, isAr)}
                </Badge>
                <p className="text-sm whitespace-pre-wrap break-words">{content.progress.summary}</p>
                {content.progress.blocker ? (
                  <p className="text-xs text-muted-foreground break-words">
                    {t("Blocker", "العائق")}: {content.progress.blocker}
                  </p>
                ) : null}
                {content.progress.nextStep ? (
                  <p className="text-xs text-muted-foreground break-words">
                    {t("Next step", "الخطوة التالية")}: {content.progress.nextStep}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
        </>
      ) : (
      <div
        className="rounded-3xl border border-border/60 bg-card p-5 shadow-sm"
        data-testid="report-update-section"
      >
        <h3 className="font-medium text-sm mb-2">{t("Weekly progress", "التقدم الأسبوعي")}</h3>
        {content.progress == null ? (
          <p className="text-sm text-muted-foreground" data-testid="report-update-empty">
            {t("No weekly team update yet.", "لا يوجد تحديث أسبوعي للفريق بعد.")}
          </p>
        ) : (
          <div className="space-y-2">
            <Badge
              variant="secondary"
              className={cn("text-[11px]", statusToneClass(content.progress.status))}
              data-testid="report-update-status"
            >
              {statusLabel(content.progress.status, isAr)}
            </Badge>
            <p className="text-sm whitespace-pre-wrap break-words">{content.progress.summary}</p>
            {content.progress.blocker ? (
              <div>
                <span className="text-[11px] font-medium text-muted-foreground block">
                  {t("Blocker", "العائق")}
                </span>
                <p className="text-sm whitespace-pre-wrap break-words">{content.progress.blocker}</p>
              </div>
            ) : null}
            {content.progress.nextStep ? (
              <div>
                <span className="text-[11px] font-medium text-muted-foreground block">
                  {t("Next step", "الخطوة التالية")}
                </span>
                <p className="text-sm whitespace-pre-wrap break-words">{content.progress.nextStep}</p>
              </div>
            ) : null}
          </div>
        )}
      </div>
      )}

      <div
        className="rounded-3xl border border-border/60 bg-card p-5 shadow-sm"
        data-testid="report-receipts-section"
      >
        <h3 className="font-medium text-sm mb-2">
          {t("Shared receipt highlights", "أبرز الملخّصات المُشارَكة")}
        </h3>
        {content.highlights.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="report-receipts-empty">
            {t("No shared receipt highlights yet.", "لا توجد ملخّصات مُشارَكة بارزة بعد.")}
          </p>
        ) : (
          <ul className="space-y-3">
            {content.highlights.map((h, i) => (
              <li key={i} data-testid={`report-highlight-${i}`}>
                <span className="text-sm font-medium break-words">{h.headline}</span>
                {h.lines.length > 0 ? (
                  <ul className="mt-1 space-y-0.5">
                    {h.lines.map((l, j) => (
                      <li key={j} className="text-xs text-muted-foreground break-words">
                        {l}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* W4 — Direction Overview is SUPPORTING context, not the main report.
          A native <details> disclosure collapses it by default on mobile
          (open on sm+ via `sm:open`-style CSS, and forced open for print so
          the printed document is complete). No numeric count in the label;
          the disclosure state is UI-only and never affects stored content. */}
      <details
        ref={directionsRef}
        className="group rounded-3xl border border-border/60 bg-card p-5 shadow-sm"
        data-testid="report-directions-section"
      >
        <summary
          className="flex cursor-pointer list-none items-center justify-between gap-2 font-medium text-sm marker:hidden"
          data-testid="report-directions-toggle"
        >
          <span>{t("Direction overview", "نظرة عامة على التوجيه")}</span>
          <span className="text-[11px] font-normal text-muted-foreground print:hidden">
            <span className="inline group-open:hidden">{t("View Direction Overview", "عرض نظرة عامة على التوجيه")}</span>
            <span className="hidden group-open:inline">{t("Hide Direction Overview", "إخفاء نظرة عامة على التوجيه")}</span>
          </span>
        </summary>
        {/* print:!block force-opens the content for the printed document even
            when the disclosure is collapsed on screen (beats the UA
            details:not([open]) hide rule via !important). */}
        <div className="mt-3 print:!block">
          {content.directions.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="report-directions-empty">
              {t("No active team direction.", "لا يوجد توجيه نشط للفريق.")}
            </p>
          ) : (
            <ul className="space-y-2">
              {content.directions.map((d, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`report-direction-${i}`}>
                  <span className="min-w-0 flex-1 text-sm break-words">{d.title}</span>
                  <Badge
                    variant="secondary"
                    className={cn("text-[10px] shrink-0", importanceToneClass(d.importance))}
                  >
                    {impLabel(d.importance, isAr)}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </div>
      </details>

      {/* Trust footer — the report's one-line provenance promise (W3-approved
          full wording), on screen and in print (the print header carries the
          declared-progress line too). Rendered ONCE per report. */}
      <p className="text-[11px] text-muted-foreground px-1 pb-2" data-testid="report-trust-footer">
        {t(
          "Built from shared Team Direction, weekly focus, human-declared status, approved receipts, and any additional weekly context provided. Nothing here was computed from private work. Status is declared by an authorised team lead or manager and is not calculated by AI.",
          "مبني على توجيه الفريق المُشارَك، وتركيز الأسبوع، والحالة المُعلنة بشريًا، والملخّصات المعتمدة، وأي سياق أسبوعي إضافي متوفر. لا شيء هنا محسوب من عمل خاص. الحالة يُعلنها قائد فريق أو مدير مخوَّل، ولا يحسبها الذكاء الاصطناعي.",
        )}
      </p>
    </>
  );
}

export default function TeamReport() {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const { isAuthenticated } = useAuth();
  const ws = useActiveWorkspace();
  const teamId = ws.active?.team.id ?? null;
  const teamName = ws.active?.team.name;
  const isPersonal = !!ws.active?.team.isPersonal;
  const t = (en: string, ar: string) => (isAr ? ar : en);

  const prefs = trpc.preferences.me.useQuery(undefined, { enabled: isAuthenticated });
  const weekStartDay = prefs.data?.weekStartDay ?? 0;
  const [weekOffset, setWeekOffset] = useState(initialWeekOffsetFromUrl);
  // The raw ?week= target, captured BEFORE the sync effect can rewrite the
  // URL. The Sunday-based initial offset above is a first guess only: for
  // weekStartDay > 0 it decodes one week early whenever todayUTCDay <
  // weekStartDay, which the Team ⇄ Report round-trip would compound. Once the
  // preference is known, re-derive the EXACT offset against the same base.
  const [initialWeekTarget] = useState(initialWeekTargetFromUrl);
  useEffect(() => {
    if (initialWeekTarget == null) return;
    setWeekOffset(weekOffsetForTarget(initialWeekTarget, weekStartDay, NOW));
  }, [initialWeekTarget, weekStartDay]);
  const weekStart = useMemo(() => {
    const b = weekStartUTC(NOW, weekStartDay);
    b.setUTCDate(b.getUTCDate() + weekOffset * 7);
    return b;
  }, [weekStartDay, weekOffset]);
  const weekStartKey = useMemo(
    () => weekKey(weekStart, weekStartDay),
    [weekStart, weekStartDay],
  );
  // URL follows the viewed week, so Back (in-app or browser) and refresh
  // return to the week the user was actually reading.
  useEffect(() => syncWeekUrlParam(weekStartKey), [weekStartKey]);
  const isCurrentWeek = weekOffset === 0;

  // Authority views toggle between approved/draft; staleness banner dismissal
  // is per-mount, per-week ("Keep current report" hides it until regenerated).
  // A `?view=draft` hint (only ever set by the wizard's generate-return path)
  // makes a freshly generated updated draft the default view; every other entry
  // defaults to the approved report first. Read ONCE at mount.
  const [view, setView] = useState<"approved" | "draft">(() =>
    typeof window !== "undefined" && /[?&]view=draft(?:&|$)/.test(window.location.search)
      ? "draft"
      : "approved",
  );
  // The initializer above already consumed `view=draft`; strip it from the URL
  // once so it's a TRUE one-time hint — a later manual switch to Approved is not
  // re-overridden on reload. `week` is untouched (syncWeekUrlParam owns it).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has("view")) return;
    url.searchParams.delete("view");
    window.history.replaceState(window.history.state, "", url);
  }, []);
  const [dismissedStaleFor, setDismissedStaleFor] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [introDraft, setIntroDraft] = useState("");

  const enabled = isAuthenticated && teamId != null && !isPersonal;
  const report = trpc.weeklyTeamReports.get.useQuery(
    { teamId: teamId!, weekStartKey },
    { enabled },
  );
  const utils = trpc.useUtils();
  const invalidate = () => utils.weeklyTeamReports.get.invalidate();
  // W1 — neutral conflict handling: a CONFLICT means the report changed while
  // the user was working (regeneration, approval, reopen, or another session).
  // We refresh the page state so they can review the latest version; we never
  // retry, redirect, or merge automatically. No revision ids in any copy.
  const isConflict = (e: unknown) =>
    (e as { data?: { code?: string } } | null)?.data?.code === "CONFLICT";
  const onConflictRefresh = (e: unknown) => {
    if (isConflict(e)) invalidate();
  };
  const generate = trpc.weeklyTeamReports.generate.useMutation({
    onSuccess: () => {
      invalidate();
      setView("draft");
      setDismissedStaleFor(null);
    },
    onError: onConflictRefresh,
  });
  const saveEdits = trpc.weeklyTeamReports.saveDraftEdits.useMutation({
    onSuccess: () => {
      invalidate();
      setEditorOpen(false);
    },
    // On conflict the dialog stays open with the local introduction intact —
    // the page refreshes underneath so the user reviews the latest draft
    // before deciding to retry.
    onError: onConflictRefresh,
  });
  const approve = trpc.weeklyTeamReports.approve.useMutation({
    onSuccess: () => {
      invalidate();
      setView("approved");
    },
    onError: onConflictRefresh,
  });
  const reopen = trpc.weeklyTeamReports.reopen.useMutation({
    onSuccess: () => {
      invalidate();
      setView("draft");
    },
    onError: onConflictRefresh,
  });
  // Phase L — "Create updated draft" opens an editable INPUT cycle (it does NOT
  // regenerate here): the approved/draft revision is preserved, inputs unlock,
  // and we route to the wizard on the Team page to edit focus + progress and
  // then Generate. Non-destructive, so no human-edit confirmation is needed.
  const [, navigate] = useLocation();
  const openCycle = trpc.weeklyTeamReports.openUpdatedDraftCycle.useMutation({
    onSuccess: () => {
      invalidate();
      // Contextual return: land on the Team page IN the updated-draft flow (the
      // banner + wizard focus), never a bare generic Team page. Week preserved.
      navigate(`/team?week=${weekStartKey}&reportFlow=updated-draft`);
    },
    onError: onConflictRefresh,
  });
  const requestUpdatedDraft = () =>
    openCycle.mutate({ teamId: teamId!, weekStartKey });
  // W1 — the ONE neutral conflict message (per-surface variant for generate's
  // draft-exists case). Derived from mutation errors so a stale action shows
  // it and a later successful action clears it.
  const conflictMessage = t(
    "This report changed while you were working. Review the latest version before continuing.",
    "تغيّر هذا التقرير أثناء عملك. راجع أحدث نسخة قبل المتابعة.",
  );
  const draftExistsMessage = t(
    "A draft already exists. Review it or generate a new version.",
    "توجد مسودة بالفعل. راجعها أو أنشئ نسخة جديدة.",
  );
  // Phase L — the report page no longer generates; its conflict-producing
  // actions are Create updated draft (openCycle), approve, and reopen. A
  // concurrent change surfaces the ONE neutral notice (generate's draft-exists
  // variant is retained for the rare direct-generate path).
  const pageConflictNotice =
    isConflict(openCycle.error) || isConflict(approve.error) || isConflict(reopen.error)
      ? conflictMessage
      : isConflict(generate.error)
        ? (generate.error as { message?: string } | null)?.message === "report_draft_exists"
          ? draftExistsMessage
          : conflictMessage
        : null;
  // Stage 6D — email flow: preview loads LAZILY when the dialog opens
  // (authority-gated server-side); send requires the literal confirm.
  const [emailOpen, setEmailOpen] = useState(false);
  // P4 — print-preparation guidance. Browser-generated print headers/footers
  // (title/date/URL/page number) CANNOT be removed by app CSS, so before
  // invoking print we show a lightweight dialog telling the user to turn them
  // off. Opening/confirming/cancelling this dialog NEVER mutates lifecycle.
  const [printPrepOpen, setPrintPrepOpen] = useState(false);
  // Shared print helper for EVERY Weekly Report print trigger (member + authority,
  // approved + draft). Safari prints the active text-selection HIGHLIGHT
  // (::selection) across page boundaries when "Print backgrounds" is on — a gray
  // block from mid-Weekly-Progress onward (it vanishes with backgrounds off).
  // So we CLEAR the selection + blur focus, wait two animation frames for the
  // DOM/selection state to settle, then print EXACTLY ONCE. No lifecycle
  // mutation; the selected approved/draft view is preserved.
  const prepareAndPrintWeeklyReport = () => {
    setPrintPrepOpen(false); // close the guidance dialog (print:hidden regardless)
    if (typeof window === "undefined") return;
    window.getSelection()?.removeAllRanges();
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
      });
    });
  };
  const emailPreview = trpc.weeklyTeamReports.emailPreview.useQuery(
    { teamId: teamId!, weekStartKey },
    { enabled: enabled && emailOpen },
  );
  const emailSend = trpc.weeklyTeamReports.emailSend.useMutation();

  const weekLabel = useMemo(() => {
    const end = new Date(weekStart);
    end.setUTCDate(weekStart.getUTCDate() + 6);
    const f = (d: Date) =>
      d.toLocaleDateString(isAr ? "ar-u-nu-latn" : "en", { month: "short", day: "numeric", timeZone: "UTC" });
    return `${f(weekStart)} — ${f(end)}`;
  }, [weekStart, isAr]);

  // R2 — project the SELECTED report content into the allow-listed print model
  // (approved OR draft, never both) for the dedicated print document below.
  // Members only ever reach the approved branch (their payload carries no
  // draft), so a member can never instantiate a draft print document.
  const printModel = useMemo(() => {
    const d = report.data as GetOut | undefined;
    if (!d) return null;
    const fmtDate = (x: Date) =>
      new Date(x).toLocaleDateString(isAr ? "ar" : "en", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    if (!("draft" in d)) {
      // MEMBER: approved only.
      return d.approved
        ? toPrintModel({
            content: d.approved.content,
            status: "approved",
            teamName: teamName ?? "",
            weekLabel,
            approvedDate: fmtDate(new Date(d.approved.approvedAt)),
            isAr,
          })
        : null;
    }
    const auth = d as AuthorityOut;
    const showingDraftNow =
      auth.status === "draft" || (auth.status === "approved_with_draft" && view === "draft");
    if (showingDraftNow && auth.draft) {
      return toPrintModel({
        content: auth.draft.content,
        status: "draft",
        teamName: teamName ?? "",
        weekLabel,
        isAr,
      });
    }
    if (auth.approved) {
      return toPrintModel({
        content: auth.approved.content,
        status: "approved",
        teamName: teamName ?? "",
        weekLabel,
        approvedDate: fmtDate(new Date(auth.approved.approvedAt)),
        isAr,
      });
    }
    return null;
  }, [report.data, view, teamName, weekLabel, isAr]);

  const weekNav = (
    <div className="flex items-center gap-2">
      <button
        onClick={() => setWeekOffset(o => o - 1)}
        aria-label={isAr ? "الأسبوع السابق" : "Previous week"}
        className="rounded-full p-1 hover:bg-muted"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <span className="tabular-nums">{weekLabel}</span>
      <button
        onClick={() => setWeekOffset(o => o + 1)}
        aria-label={isAr ? "الأسبوع التالي" : "Next week"}
        className="rounded-full p-1 hover:bg-muted"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
      {!isCurrentWeek ? (
        <button
          onClick={() => setWeekOffset(0)}
          className="ms-1 inline-flex items-center gap-1 rounded-full bg-coral-soft/60 text-coral px-2.5 py-0.5 text-xs font-medium hover:bg-coral-soft"
        >
          <RotateCcw className="h-3 w-3" /> {isAr ? "هذا الأسبوع" : "This week"}
        </button>
      ) : null}
    </div>
  );

  {/* W4 — the page header already titles the page, so the framing card no
      longer repeats a large "Weekly team report" heading (screenshot fix #1/#2:
      too much explanatory content up top). The sources + principles live in a
      COLLAPSED "How this report is built" disclosure, out of the way of the
      report itself, but present for anyone who wants the provenance. */}
  const framing = (
    <details
      className="group print:hidden rounded-2xl border border-border/60 bg-muted/30 px-4 py-2.5"
      data-testid="report-framing"
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 text-xs font-medium text-muted-foreground marker:hidden">
        <FileText className="h-3.5 w-3.5 text-coral shrink-0" />
        {t("How this report is built", "كيف يُبنى هذا التقرير")}
        <ChevronRight className="h-3.5 w-3.5 ms-auto transition-transform group-open:rotate-90 rtl:group-open:-rotate-90" />
      </summary>
      <div className="mt-2 space-y-1">
        <p className="text-xs text-muted-foreground">
          {t(
            "This report is generated from shared Team Direction, weekly focus, human-declared status, approved receipts, and any additional weekly context provided for the selected week.",
            "يُنشأ هذا التقرير من توجيه الفريق المُشارَك، وتركيز الأسبوع، والحالة المُعلنة بشريًا، والملخّصات المعتمدة، وأي سياق أسبوعي إضافي متوفر للأسبوع المحدد.",
          )}
        </p>
        <p className="text-[11px] text-muted-foreground" data-testid="report-sources">
          {t("Team Direction", "توجيه الفريق")}
          {" · "}
          {t("Focus this week", "تركيز هذا الأسبوع")}
          {" · "}
          {t("Human-declared weekly status", "الحالة الأسبوعية المُعلنة بشريًا")}
          {" · "}
          {t("Approved receipts", "الملخّصات المعتمدة")}
          {" · "}
          {t("Additional weekly context, when provided", "سياق أسبوعي إضافي، عند توفره")}
        </p>
        <p className="text-[11px] text-muted-foreground" data-testid="report-declared-principle">
          {t(
            "Status is declared by an authorised team lead or manager and is not calculated by AI.",
            "الحالة يُعلنها قائد فريق أو مدير مخوَّل، ولا يحسبها الذكاء الاصطناعي.",
          )}
        </p>
        <p className="text-[11px] text-muted-foreground">
          {t(
            "This report does not use private personal priorities or private links.",
            "لا يستخدم هذا التقرير الأولويات الشخصية الخاصة ولا الروابط الخاصة.",
          )}
        </p>
      </div>
    </details>
  );

  if (isAuthenticated && (teamId == null || isPersonal)) {
    return (
      <TabScaffold
        title={t("Weekly team report", "تقرير الفريق الأسبوعي")}
        topBar={<AppTopBar />}
        state={{ isLoading: false, isError: false, data: {}, refetch: () => {} }}
        isEmpty={() => true}
        emptyTitle={t("No team selected", "لم يتم اختيار فريق")}
        emptyHint={t(
          "Switch to a team workspace to see the shared weekly team report.",
          "انتقل إلى مساحة عمل فريق لرؤية تقرير الفريق الأسبوعي المُشارَك.",
        )}
      >
        {() => null}
      </TabScaffold>
    );
  }

  return (
    <>
    {/* The on-screen app page is STRUCTURALLY excluded from print (display:none
        via `contents print:hidden`); only the dedicated print document below
        prints. `contents` keeps the on-screen layout unchanged. */}
    <div className="contents print:hidden">
    <TabScaffold
      title={t("Weekly team report", "تقرير الفريق الأسبوعي")}
      topBar={<AppTopBar />}
      subtitle={
        <div className="flex flex-col gap-0.5">
          {teamName ? <span>{teamName}</span> : null}
          {weekNav}
        </div>
      }
      headerRight={
        <Link
          href={`/team?week=${weekStartKey}`}
          data-testid="report-back"
          className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-medium hover:bg-muted/70"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {t("Team", "الفريق")}
        </Link>
      }
      state={report}
      // MEMBERS with nothing approved land on the empty state; authority always
      // renders (the not_generated state carries the Generate call-to-action).
      isEmpty={d => !("draft" in d) && d.approved == null}
      emptyTitle={t("No approved report yet for this week.", "لا يوجد تقرير معتمد لهذا الأسبوع بعد.")}
      emptyHint={t(
        "A shared weekly view built from team focus, team updates, and approved receipts.",
        "عرض أسبوعي مُشارَك مبني على تركيز الفريق وتحديثاته والملخّصات المعتمدة.",
      )}
    >
      {d => {
        const isAuthority = "draft" in d;
        const approved = d.approved;

        // ---- MEMBER: approved report only (draft keys never even arrive) ----
        if (!isAuthority) {
          return (
            <>
              {framing}
              {approved ? (
                <>
                  <p className="text-[11px] text-muted-foreground px-1" data-testid="report-approved-at">
                    {t("Approved", "اعتُمد")}{" "}
                    {new Date(approved.approvedAt).toLocaleDateString(isAr ? "ar" : "en", {
                      month: "short",
                      day: "numeric",
                    })}
                  </p>
                  <ReportContentView content={approved.content} isAr={isAr} />
                  {/* Stage 6D — members may download/print the APPROVED report
                      (browser print-to-PDF). Content-first: the action sits
                      AFTER the report body, matching the authority view. */}
                  <div className="print:hidden pt-3 border-t border-border/60">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 rounded-full text-xs"
                      data-testid="report-download"
                      onClick={() => setPrintPrepOpen(true)}
                    >
                      <Printer className="h-3.5 w-3.5 me-1" />
                      {t("Download or print", "تنزيل أو طباعة")}
                    </Button>
                  </div>
                </>
              ) : null}
              <div className="pb-4" aria-hidden />
            </>
          );
        }

        // ---- AUTHORITY ----
        const auth = d as AuthorityOut;
        const draft = auth.draft ?? null;
        const showingDraft =
          (auth.status === "draft") ||
          (auth.status === "approved_with_draft" && view === "draft");
        const staleDismissed = dismissedStaleFor === weekStartKey;

        return (
          <>
            {framing}

            {/* W1 — neutral concurrency notice (stale generate/approve/reopen) */}
            {pageConflictNotice ? (
              <div
                className="print:hidden rounded-2xl border border-border/60 bg-muted/40 p-3"
                data-testid="report-conflict"
              >
                <p className="text-xs text-muted-foreground">{pageConflictNotice}</p>
              </div>
            ) : null}

            {/* not_generated → point back to the ONE report workflow (the
                Weekly Report wizard on the Team page). W1: there is a single
                generation action; this page never starts a competing one. */}
            {auth.status === "not_generated" ? (
              <div
                className="rounded-3xl border border-border/60 bg-card p-5 shadow-sm text-center"
                data-testid="report-generate-cta"
              >
                <p className="text-sm text-muted-foreground mb-3">
                  {t(
                    "This week’s report is prepared on the Team page.",
                    "يُجهَّز تقرير هذا الأسبوع من صفحة الفريق.",
                  )}
                </p>
                <a
                  href={`/team?week=${weekStartKey}`}
                  data-testid="report-generate-from-team"
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
                >
                  <FileText className="h-4 w-4" />
                  {t("Prepare on the Team page", "التجهيز من صفحة الفريق")}
                </a>
              </div>
            ) : null}

            {/* approved + newer draft → ONE compact status line + a proper
                view switch (tabs). The approved report stays visible while the
                updated draft is prepared; the "Updated draft" tab IS the
                "view updated draft" affordance (no duplicate primary button
                competing with it). Role-accurate: never "only you". */}
            {auth.status === "approved_with_draft" ? (
              <div className="print:hidden" data-testid="report-updated-draft-status">
                <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs">
                  <span className="inline-flex items-center gap-1.5 font-medium text-emerald-700 dark:text-emerald-300">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {t("Approved report remains visible", "التقرير المعتمد يبقى ظاهرًا")}
                  </span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground">
                    {t("Updated draft in preparation", "مسودة محدَّثة قيد الإعداد")}
                  </span>
                </p>
                <div
                  role="tablist"
                  aria-label={t("Report view", "عرض التقرير")}
                  className="mt-2 inline-flex rounded-full border border-border/60 bg-muted/40 p-0.5"
                  data-testid="report-view-toggle"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={view === "approved"}
                    className={cn(
                      "rounded-full px-3 py-1 text-xs font-medium transition",
                      view === "approved" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground",
                    )}
                    data-testid="report-view-approved"
                    onClick={() => setView("approved")}
                  >
                    {t("Approved report", "التقرير المعتمد")}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={view === "draft"}
                    className={cn(
                      "rounded-full px-3 py-1 text-xs font-medium transition",
                      view === "draft" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground",
                    )}
                    data-testid="report-view-draft"
                    onClick={() => setView("draft")}
                  >
                    {t("Updated draft", "المسودة المحدَّثة")}
                  </button>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {t(
                    "The approved report stays visible while the updated draft is prepared.",
                    "يبقى التقرير المعتمد ظاهرًا أثناء إعداد المسودة المحدَّثة.",
                  )}
                </p>
              </div>
            ) : null}

            {/* Compact shared-input notice — shown ONLY when an APPROVED report
                exists (no draft yet) and new shared inputs arrived, so the
                "approved report will remain visible" promise is literally true
                and we never imply an updated draft already exists. Neutral,
                non-dominant; it never says the approved report is wrong. */}
            {auth.status === "approved" && auth.staleForApproved && !staleDismissed ? (
              <div
                className="print:hidden rounded-2xl border border-border/60 bg-muted/40 p-3"
                data-testid="report-stale-banner"
                role="status"
              >
                <p className="text-xs font-medium" data-testid="report-stale-title">
                  {t("New shared inputs are available", "تتوفر مدخلات مُشارَكة جديدة")}
                </p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {t(
                    "Create an updated draft to include them. The approved report will remain visible.",
                    "أنشئ مسودة محدَّثة لتضمينها. سيبقى التقرير المعتمد ظاهرًا.",
                  )}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    className="h-8 rounded-full text-xs"
                    disabled={openCycle.isPending}
                    data-testid="report-stale-regenerate"
                    onClick={requestUpdatedDraft}
                  >
                    <RefreshCw className="h-3.5 w-3.5 me-1" />
                    {t("Create updated draft", "إنشاء مسودة محدَّثة")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 rounded-full text-xs text-muted-foreground"
                    data-testid="report-stale-keep"
                    onClick={() => setDismissedStaleFor(weekStartKey)}
                  >
                    {t("Keep current report", "الإبقاء على التقرير الحالي")}
                  </Button>
                </div>
              </div>
            ) : null}

            {/* DRAFT banner + controls */}
            {showingDraft && draft ? (
              <>
                {/* Compact draft status — one small line. Role-accurate
                    ("leads", never "only you") + the safe generation-mode hint
                    (never meta/debug). Approved-with-draft's status line above
                    already frames it, so this stays minimal. */}
                <div className="print:hidden" data-testid="report-draft-banner">
                  <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                    <Badge variant="outline" className="text-[10px]">
                      {t("Draft", "مسودة")}
                    </Badge>
                    <span className="text-muted-foreground">
                      {t("Only visible to leads until approved", "ظاهرة للقادة فقط حتى الاعتماد")}
                    </span>
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground" data-testid="report-draft-mode">
                    {draft.generationMode === "llm"
                      ? t("AI draft. Review before approving.", "مسودة ذكاء اصطناعي. راجعها قبل الاعتماد.")
                      : t("Deterministic draft.", "مسودة قياسية.")}
                  </p>
                </div>
                {/* W4 — draft context: the AI-authored intro is labelled
                    "AI-prepared draft"; human-declared status is never AI-labelled. */}
                <ReportContentView content={draft.content} isAr={isAr} aiPrepared />
                {/* SINGLE PRIMARY draft action = Approve, in a sticky bar that
                    floats above the fixed bottom nav (safe-area aware) and is
                    authority-only + print-hidden. Secondary actions (Edit intro,
                    Create updated draft, Print draft) live in an accessible
                    native-details "More" menu so they never compete as equal
                    primary buttons — content stays first. */}
                <div
                  className="print:hidden sticky z-30 -mx-4 mt-2 border-t border-border/60 bg-background/90 px-4 py-2 backdrop-blur-md"
                  style={{
                    bottom: "calc(4.75rem + env(safe-area-inset-bottom))",
                  }}
                  data-testid="report-sticky-actions"
                >
                  <div className="flex items-center gap-2">
                    <Button
                      className="flex-1 rounded-full"
                      disabled={approve.isPending}
                      data-testid="report-approve"
                      onClick={() =>
                        approve.mutate({
                          teamId: teamId!,
                          weekStartKey,
                          // W1 token: approve exactly the reviewed revision
                          approveRevisionNumber: draft.revisionNumber,
                        })
                      }
                    >
                      <CheckCircle2 className="h-4 w-4 me-1.5" />
                      {auth.status === "approved_with_draft"
                        ? t("Approve updated draft", "اعتماد المسودة المحدَّثة")
                        : t("Approve report", "اعتماد التقرير")}
                    </Button>
                    <details className="group relative shrink-0" data-testid="report-more">
                      <summary
                        className="flex h-10 cursor-pointer list-none items-center gap-1 rounded-full border border-border/60 px-3.5 text-xs font-medium marker:hidden hover:bg-muted"
                        aria-label={t("More report actions", "المزيد من إجراءات التقرير")}
                        data-testid="report-more-toggle"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                        {t("More", "المزيد")}
                      </summary>
                      <div
                        className="absolute end-0 bottom-full mb-2 min-w-44 rounded-2xl border border-border/60 bg-card p-1 shadow-lg"
                        role="menu"
                        data-testid="report-more-menu"
                      >
                        <button
                          role="menuitem"
                          className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-start text-xs hover:bg-muted"
                          data-testid="report-edit"
                          onClick={() => {
                            setIntroDraft(draft.content.intro ?? "");
                            setEditorOpen(true);
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" /> {t("Edit introduction", "تعديل المقدمة")}
                        </button>
                        <button
                          role="menuitem"
                          className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-start text-xs hover:bg-muted disabled:opacity-50"
                          disabled={openCycle.isPending}
                          data-testid="report-regenerate"
                          onClick={requestUpdatedDraft}
                        >
                          <RefreshCw className="h-3.5 w-3.5" /> {t("Create updated draft", "إنشاء مسودة محدَّثة")}
                        </button>
                        <button
                          role="menuitem"
                          className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-start text-xs hover:bg-muted"
                          data-testid="report-print-draft"
                          onClick={() => setPrintPrepOpen(true)}
                        >
                          <Printer className="h-3.5 w-3.5" /> {t("Print draft", "طباعة المسودة")}
                        </button>
                      </div>
                    </details>
                  </div>
                </div>
              </>
            ) : null}

            {/* APPROVED view — compact status + the ONE primary action near the
                status (create an updated draft), then the report CONTENT starts.
                Secondary actions (download/email/reopen) live in a toolbar AFTER
                the report, so the page reads content-first. */}
            {!showingDraft && approved ? (
              <>
                <div className="print:hidden" data-testid="report-approved-banner">
                  <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                    <Badge
                      variant="secondary"
                      className="text-[10px] bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                    >
                      {t("Approved", "معتمد")}
                    </Badge>
                    <span className="text-muted-foreground">
                      {t("Visible to the team.", "ظاهر للفريق.")}{" "}
                      {t("Approved", "اعتُمد")}{" "}
                      {new Date(approved.approvedAt).toLocaleDateString(isAr ? "ar" : "en", {
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  </p>
                  {/* One primary action, only when no updated draft exists yet
                      (with a newer draft, the view switch above is the path).
                      When the shared-input notice is showing it already carries
                      the "Create updated draft" primary, so we don't duplicate
                      it here — exactly one primary near the status at a time. */}
                  {auth.status === "approved" && !(auth.staleForApproved && !staleDismissed) ? (
                    <Button
                      size="sm"
                      className="mt-2 h-8 rounded-full text-xs"
                      disabled={openCycle.isPending}
                      data-testid="report-regenerate-approved"
                      onClick={requestUpdatedDraft}
                    >
                      <RefreshCw className="h-3.5 w-3.5 me-1" /> {t("Create updated draft", "إنشاء مسودة محدَّثة")}
                    </Button>
                  ) : null}
                </div>
                <ReportContentView content={approved.content} isAr={isAr} />
                {/* Secondary authority actions — AFTER the report, compact toolbar. */}
                <div
                  className="print:hidden flex flex-wrap gap-2 border-t border-border/60 pt-3"
                  data-testid="report-approved-actions"
                >
                  {/* Stage 6D — download/print + members-only email. */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 rounded-full text-xs"
                    data-testid="report-download"
                    onClick={() => setPrintPrepOpen(true)}
                  >
                    <Printer className="h-3.5 w-3.5 me-1" />
                    {t("Download or print", "تنزيل أو طباعة")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 rounded-full text-xs"
                    data-testid="report-email"
                    onClick={() => setEmailOpen(true)}
                  >
                    <Mail className="h-3.5 w-3.5 me-1" /> {t("Email to team", "إرسال للفريق")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 rounded-full text-xs text-muted-foreground"
                    disabled={reopen.isPending}
                    data-testid="report-reopen"
                    onClick={() =>
                      reopen.mutate({
                        teamId: teamId!,
                        weekStartKey,
                        // W1 token: reopen exactly the approved revision on screen
                        expectedApprovedRevisionNumber: approved.revisionNumber,
                      })
                    }
                  >
                    <Undo2 className="h-3.5 w-3.5 me-1" /> {t("Reopen", "إعادة فتح")}
                  </Button>
                </div>
              </>
            ) : null}

            {/* Stage 6D — email preview + explicit confirmation. Recipients
                are TEAM MEMBERS ONLY, resolved server-side; only the COUNT is
                shown (operational delivery info) — never names or addresses. */}
            <Dialog open={emailOpen} onOpenChange={o => setEmailOpen(o)}>
              <DialogContent data-testid="report-email-dialog">
                <DialogHeader>
                  <DialogTitle>{t("Email to team", "إرسال للفريق")}</DialogTitle>
                  <DialogDescription>
                    {t(
                      "This will send the approved report to team members by email.",
                      "سيؤدي هذا إلى إرسال التقرير المعتمد إلى أعضاء الفريق عبر البريد الإلكتروني.",
                    )}
                  </DialogDescription>
                </DialogHeader>
                {emailPreview.data ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium break-words" data-testid="report-email-subject">
                      {emailPreview.data.subject}
                    </p>
                    <pre
                      className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg bg-muted/40 p-3 text-xs"
                      data-testid="report-email-body"
                    >
                      {emailPreview.data.textPreview}
                    </pre>
                    <p className="text-xs text-muted-foreground" data-testid="report-email-count">
                      {t("Recipients", "المستلمون")}: {emailPreview.data.recipientCount}
                    </p>
                    {!emailPreview.data.emailConfigured ? (
                      <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="report-email-unconfigured">
                        {t(
                          "Email is not configured on this server, so sending is disabled.",
                          "البريد الإلكتروني غير مُهيأ على هذا الخادم، لذا الإرسال معطَّل.",
                        )}
                      </p>
                    ) : null}
                    {emailSend.data ? (
                      <p className="text-xs text-emerald-700 dark:text-emerald-300" data-testid="report-email-result">
                        {t("Sent", "أُرسل")}: {emailSend.data.sent} · {t("Failed", "فشل")}: {emailSend.data.failed}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t("Loading preview", "جارٍ تحميل المعاينة")}</p>
                )}
                <DialogFooter className="mt-2">
                  <Button variant="ghost" data-testid="report-email-cancel" onClick={() => setEmailOpen(false)}>
                    {t("Cancel", "إلغاء")}
                  </Button>
                  <Button
                    disabled={
                      emailSend.isPending ||
                      !emailPreview.data ||
                      !emailPreview.data.emailConfigured ||
                      emailPreview.data.recipientCount === 0 ||
                      !!emailSend.data
                    }
                    data-testid="report-email-confirm"
                    onClick={() => emailSend.mutate({ teamId: teamId!, weekStartKey, confirm: true })}
                  >
                    <Mail className="h-3.5 w-3.5 me-1" /> {t("Confirm send", "تأكيد الإرسال")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Edit dialog — v1 edits the intro note; sections stay generated. */}
            <Dialog open={editorOpen} onOpenChange={o => setEditorOpen(o)}>
              <DialogContent data-testid="report-editor">
                <DialogHeader>
                  <DialogTitle>{t("Edit draft", "تعديل المسودة")}</DialogTitle>
                  <DialogDescription>
                    {t(
                      "Add a short intro note to this report. Sections are generated from shared team activity.",
                      "أضف مقدمة قصيرة لهذا التقرير. تُنشأ الأقسام من نشاط الفريق المُشارَك.",
                    )}
                  </DialogDescription>
                </DialogHeader>
                <Textarea
                  value={introDraft}
                  maxLength={600}
                  onChange={e => setIntroDraft(e.target.value)}
                  placeholder={t("Intro note (optional)", "مقدمة قصيرة (اختياري)")}
                  data-testid="report-editor-intro"
                />
                {/* W1 — stale-edit conflict: the dialog stays open, the local
                    intro is retained, and the page refreshed underneath. */}
                {isConflict(saveEdits.error) ? (
                  <p className="text-xs text-muted-foreground" data-testid="report-editor-conflict">
                    {conflictMessage}
                  </p>
                ) : null}
                <DialogFooter className="mt-2">
                  <Button variant="ghost" onClick={() => setEditorOpen(false)}>
                    {t("Cancel", "إلغاء")}
                  </Button>
                  <Button
                    disabled={saveEdits.isPending || !draft}
                    data-testid="report-editor-save"
                    onClick={() => {
                      if (!draft) return;
                      const next = { ...draft.content } as ReportContent & { intro?: string };
                      const trimmed = introDraft.trim();
                      if (trimmed) next.intro = trimmed;
                      else delete next.intro;
                      saveEdits.mutate({
                        teamId: teamId!,
                        weekStartKey,
                        content: next,
                        // W1 token: the edit lands only on the reviewed revision
                        baseRevisionNumber: draft.revisionNumber,
                      });
                    }}
                  >
                    {t("Save", "حفظ")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <div className="pb-4" aria-hidden />
          </>
        );
      }}
    </TabScaffold>
    {/* P4 — print-preparation guidance (browser headers/footers can't be removed
        by app CSS). Shared by member + authority download; print:hidden so it
        never appears in the printed artifact; no lifecycle mutation. */}
    <Dialog open={printPrepOpen} onOpenChange={setPrintPrepOpen}>
      <DialogContent data-testid="report-print-prep" className="print:hidden">
        <DialogHeader>
          <DialogTitle>{t("Print weekly report", "طباعة التقرير الأسبوعي")}</DialogTitle>
          <DialogDescription>
            {t(
              "For a clean PDF, turn off “Headers and footers” in your browser’s print settings.",
              "للحصول على ملف PDF نظيف، أوقف ”الرؤوس والتذييلات“ في إعدادات الطباعة في متصفحك.",
            )}
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-1 text-sm text-muted-foreground" data-testid="report-print-prep-tips">
          <li>{t("Layout: Portrait", "الاتجاه: عمودي")}</li>
          <li>{t("Paper: A4", "الورق: A4")}</li>
          <li>{t("Headers and footers: Off", "الرؤوس والتذييلات: إيقاف")}</li>
        </ul>
        <DialogFooter className="mt-2">
          <Button variant="ghost" data-testid="report-print-prep-cancel" onClick={() => setPrintPrepOpen(false)}>
            {t("Cancel", "إلغاء")}
          </Button>
          <Button data-testid="report-print-prep-continue" onClick={prepareAndPrintWeeklyReport}>
            <Printer className="h-3.5 w-3.5 me-1" /> {t("Continue to print", "المتابعة إلى الطباعة")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </div>
    {/* Dedicated print document — the ONLY thing that prints. Built from the
        allow-listed print model of the SELECTED view (approved OR draft). */}
    {printModel ? <WeeklyReportPrintDocument model={printModel} /> : null}
    </>
  );
}
